import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import HtmlParser, {
  domToReact,
  HTMLReactParserOptions,
} from "html-react-parser";
import {
  $Button,
  $Collapse,
  $Drawer,
  $Skeleton,
  Pagination,
  $TextArea,
  $Alert,
  $Divider,
  $Popconfirm,
  $Tooltip,
} from "us.common/components";
import { CheckCircleFilled, StarFilled, StarOutlined } from "us.icons";
import "./TemplateEditor.scss";
import { DomainMessageType } from "us.collection.case/constants/NotesAndMessage";
import { IPDFTemplateEditor } from "./Interfaces";
import {
  getEditableContent,
  getMainContentForNavigatePage,
  getMessageContent,
  getTockenDataValue,
  isEditablePage,
  setHTMLCode,
  setIframeContent,
} from "../../Functions";
import ReactDOMServer from "react-dom/server";
import { SendDashboardMessage } from "us.collection.case/repository";

/**
 * @description - Manage domain message template view and add/edit custom message
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/vKgD74HP
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 23/08/2024
 */

const PDFTemplateEditor: React.FC<IPDFTemplateEditor> = (props) => {
  const { t } = useTranslation();
  const {
    closeTemplateDrawer,
    openTemplateDrawer,
    domainMessageFormattedTemplate,
    domainMessageInitial,
    documentType,
    messageContentDetails,
    send,
    metaData,
    selectAttachment,
    isValidationRequired,
    handleTemplateDrawer,
    setDomainTemplate,
    dashboardTemplates,
    defaultTemplateId,
  } = props;

  const { caseId, entityType } = metaData.data ?? {};
  const { messages: templateHtmlContent, dataItems } =
    messageContentDetails?.data ?? {};

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [selectedCustomTemplate, setSelectedCustomTemplate] = useState<
    string | string[]
  >("");
  const [editableMessageContent, setEditableMessageContent] = useState({
    editContentId: 0,
    content: "",
  });
  const [customText, setCustomText] = useState<string>("");

  useEffect(() => {
    if (templateHtmlContent?.length) {
      if (document.getElementById("editor")) {
        setHTMLCode(templateHtmlContent[0], "");
      }
      setEditableMessageContent(getEditableContent(templateHtmlContent));
    }
  }, [messageContentDetails.data]);

  /**
   * Actual data set to template params
   */
  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      if (domNode?.attribs && domNode.name === "params") {
        return (
          <>
            {domToReact(domNode.children, {
              replace: (domNode) => {
                return <span>{getTockenDataValue(dataItems, domNode)}</span>;
              },
            })}
          </>
        );
      }
    },
  };

  /**
   * @function
   * @description Replace editable template content with user selection
   * @param htmlStr - USC template
   * @param templateList - Custom selection text
   * @param {*} templateValue - Selected custom text value
   * @returns
   */
  const setTemplateContent = (
    htmlStr: string,
    templateList: any,
    templateValue: any
  ) => {
    try {
      if (
        currentPage + 1 == editableMessageContent.editContentId ||
        currentPage - 1 == editableMessageContent.editContentId
      ) {
        const editorId = document.getElementById("editor") as any;
        setIframeContent(editorId, htmlStr);
      } else {
        const template =
          templateList
            .find((record: any) => record.value === templateValue)
            ?.text.toString() ?? customText;
        const htmlString = ReactDOMServer.renderToString(
          <div>{HtmlParser(template.toString(), options)}</div>
        );
        if (htmlString && htmlStr) setHTMLCode(htmlStr.toString(), htmlString);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * @function
   * @description Replace editable template content with user text
   * @param {*} customText - User message
   * @returns
   */
  const setCustormMessage = (customText: string) => {
    try {
      if (customText?.length >= 0) {
        setHTMLCode(editableMessageContent.content, customText);
      }

      setSelectedCustomTemplate("");
      setCustomText(customText);
      setCurrentPage(editableMessageContent.editContentId);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Page navigation
   * @param toNext - Is next page or not
   */
  const onNavigation = (toNext: boolean) => {
    try {
      const scrollWrapper = document.getElementById("mteWrapper") as any;
      scrollWrapper.scrollTop = 0;

      if (currentPage == editableMessageContent.editContentId) {
        const editorId = document.getElementById("editor") as any;
        const innerHTML =
          editorId.contentWindow.document.documentElement.outerHTML;
        setEditableMessageContent({
          ...editableMessageContent,
          content: innerHTML,
        });
      }

      if (toNext) {
        setTemplateContent(
          getMainContentForNavigatePage(
            currentPage,
            editableMessageContent,
            templateHtmlContent,
            true
          ),
          domainMessageInitial.templateList,
          selectedCustomTemplate
        );
        setCurrentPage(currentPage + 1);
      } else {
        setTemplateContent(
          getMainContentForNavigatePage(
            currentPage,
            editableMessageContent,
            templateHtmlContent,
            false
          ),
          domainMessageInitial.templateList,
          selectedCustomTemplate
        );
        setCurrentPage(currentPage - 1);
      }
    } catch (error) {}
  };

  /**
   * Send domain message
   */
  const sendDashboardMessage = () => {
    const attachmentList: Array<string> = [];

    if (selectAttachment?.filePaths?.length) {
      selectAttachment?.filePaths?.map((file: any) => {
        attachmentList.push(file.path);
      });
    }

    send(
      SendDashboardMessage.call(
        messageContentDetails?.data,
        domainMessageInitial,
        caseId,
        entityType,
        getMessageContent(templateHtmlContent, editableMessageContent),
        attachmentList
      )
    );
    closeTemplateDrawer();
  };

  return (
    <$Drawer
      width="1320"
      title={openTemplateDrawer.title}
      placement="right"
      closable={false}
      onClose={closeTemplateDrawer}
      open={
        documentType === DomainMessageType.EMAIL
          ? openTemplateDrawer.isTemplateEditor && openTemplateDrawer.visible
          : openTemplateDrawer.visible
      }
      data-testid="edit-template"
      className="edit-pdf-template"
    >
      <div className="d-flex edit-pdf-template-body">
        <$Skeleton
          loading={
            messageContentDetails?.isLoading || dashboardTemplates?.isLoading
          }
          active
          className="mx-4 my-2"
          paragraph={{ rows: 3 }}
        >
          <div className="edit-pdf-template-side">
            <div className="epts-templates">
              <div className="epts-header">
                {t("US.COLLECTION.CASE:NOTESANDMESSAGES.SELECT_TEMPLATE")}
              </div>
              <div className="epts-body">
                <$Collapse
                  expandIcon={({ isActive }) =>
                    isActive ? (
                      <CheckCircleFilled />
                    ) : (
                      <span className="not-selected" />
                    )
                  }
                  ghost
                  accordion
                  defaultActiveKey={
                    messageContentDetails?.data?.templateId ?? 0
                  }
                  onChange={(templateId) => {
                    handleTemplateDrawer(
                      domainMessageInitial,
                      Number(templateId)
                    );
                  }}
                >
                  {dashboardTemplates?.data?.length > 0 && (
                    <>
                      {dashboardTemplates?.data?.map(
                        (dashboardTemplate: any) => (
                          <$Collapse.Panel
                            key={dashboardTemplate?.templateId}
                            header={
                              <>
                                <div className="d-flex align-items-center flex-grow-1">
                                  <$Tooltip
                                    placement="topLeft"
                                    title={dashboardTemplate?.templateName}
                                  >
                                    <div
                                      data-testid="template-list-item"
                                      className="flex-grow-1 epts-name"
                                    >
                                      {dashboardTemplate?.templateName}
                                    </div>
                                  </$Tooltip>
                                  {dashboardTemplate.templateId ===
                                    defaultTemplateId && (
                                    <$Tooltip
                                      placement="top"
                                      title={t(
                                        "US.COLLECTION.CASE:NOTESANDMESSAGES.DEFAULT_TEMPLATE"
                                      )}
                                    >
                                      <StarFilled
                                        className="text-info"
                                        style={{
                                          marginLeft: 6,
                                          marginRight: 6,
                                        }}
                                      />
                                    </$Tooltip>
                                  )}
                                  {dashboardTemplate.templateId !==
                                    defaultTemplateId && (
                                    <$Popconfirm
                                      title={t(
                                        "US.COLLECTION.CASE:NOTESANDMESSAGES.ARE_YOU_SURE_YOU_WANT_TO_CHANGE_THE_DEFAULT_TEMPLATE"
                                      )}
                                      okText={t("US.COMMON:COMMON.YES")}
                                      cancelText={t("US.COMMON:COMMON.NO")}
                                      onConfirm={(e: any) => {
                                        e.stopPropagation();
                                        setDomainTemplate(
                                          domainMessageInitial,
                                          dashboardTemplate?.templateId
                                        );
                                      }}
                                      onCancel={(e: any) => {
                                        e.stopPropagation();
                                      }}
                                      placement="right"
                                    >
                                      <$Tooltip
                                        placement="top"
                                        title={t(
                                          "US.COLLECTION.CASE:NOTESANDMESSAGES.SET_AS_DEFAULT_TEMPLATE"
                                        )}
                                      >
                                        <$Button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                          }}
                                          className="epts-actions"
                                          icon={<StarOutlined />}
                                          size="small"
                                          style={{ minWidth: 24 }}
                                        />
                                      </$Tooltip>
                                    </$Popconfirm>
                                  )}
                                </div>
                              </>
                            }
                          />
                        )
                      )}
                    </>
                  )}
                </$Collapse>
              </div>
            </div>
            <div className="epts-text">
              <div className="epts-header">
                {t("US.COLLECTION.CASE:NOTESANDMESSAGES.SELECT_TEXT")}
              </div>
              <$Skeleton
                loading={domainMessageFormattedTemplate?.isLoading}
                active
                paragraph={{ rows: 3 }}
              >
                <div className="epts-body">
                  {!isEditablePage(
                    templateHtmlContent && templateHtmlContent[0]
                  ) &&
                    isValidationRequired && (
                      <$Alert
                        message={t(
                          "US.COLLECTION.CASE:NOTESANDMESSAGES.THE_ON-HOLD_MESSAGE_IS_NOT_DEFINED_IN_THE_TEMPLATE"
                        )}
                        type="warning"
                        className="px-2 mb-3 epts-alert"
                      />
                    )}
                  <$Collapse
                    expandIcon={({ isActive }) =>
                      isActive ? (
                        <CheckCircleFilled />
                      ) : (
                        <span className="not-selected" />
                      )
                    }
                    ghost
                    accordion
                    defaultActiveKey={"customtext"}
                    onChange={(templateValue: string | string[]) => {
                      if (templateHtmlContent) {
                        setTemplateContent(
                          templateHtmlContent[0],
                          domainMessageInitial.templateList,
                          templateValue
                        );
                      }
                      setSelectedCustomTemplate(templateValue),
                        setCurrentPage(0);
                    }}
                  >
                    <$Collapse.Panel
                      header={
                        <>
                          <div className="d-flex align-items-start flex-grow-1">
                            <$Tooltip
                              placement="topLeft"
                              title={t(
                                "US.COLLECTION.CASE:NOTESANDMESSAGES.CUSTOM_TEXT"
                              )}
                            >
                              <div className="flex-grow-1 epts-name">
                                {t(
                                  "US.COLLECTION.CASE:NOTESANDMESSAGES.CUSTOM_TEXT"
                                )}
                              </div>
                            </$Tooltip>
                          </div>
                        </>
                      }
                      key={"customtext"}
                    >
                      <div className="template-text-block">
                        <$TextArea
                          name="message"
                          placeholder={t(
                            "US.COLLECTION.CASE:NOTESANDMESSAGES.TYPE_A_CUSTOM_TEXT_HERE"
                          )}
                          className="custom-message-textarea"
                          value={customText}
                          onChange={(data: any) =>
                            setCustormMessage(data.target.value)
                          }
                        />
                      </div>
                    </$Collapse.Panel>
                    <>
                      {documentType !== DomainMessageType.SMS &&
                        domainMessageInitial.templateList.map(
                          (item: any, index: any) => (
                            <$Collapse.Panel
                              header={
                                <>
                                  <div className="d-flex align-items-start flex-grow-1">
                                    <$Tooltip
                                      placement="topLeft"
                                      title={item.label}
                                    >
                                      <div className="flex-grow-1 epts-name">
                                        {item.label}
                                      </div>
                                    </$Tooltip>
                                  </div>
                                </>
                              }
                              key={item.key}
                            >
                              <div className="template-text-block">
                                {HtmlParser(item.text, options)}
                              </div>
                            </$Collapse.Panel>
                          )
                        )}
                    </>
                  </$Collapse>
                </div>
              </$Skeleton>
            </div>
          </div>
          <div
            id="mteWrapper"
            className="flex-grow-1 edit-pdf-template-content"
          >
            <div className="epts-header">
              <span className="epts-header-label text-nowrap mr-1">
                {t("US.COLLECTION.CASE:NOTESANDMESSAGES.TEMPLATE_NAME")}:
              </span>
              <span
                data-testid="template-name"
                className="epts-header-data text-truncate"
              >
                {messageContentDetails?.data?.templateName ?? ""}
              </span>
              <$Divider className="mx-3" type="vertical" />
              <span className="epts-header-label text-nowrap mr-1">
                {t(
                  "US.COLLECTION.CASE:NOTESANDMESSAGES.CONTENT_RULE_ACTIVITY_CODE"
                )}
                :
              </span>
              <span
                data-testid="template-code"
                className="epts-header-data text-nowrap"
              >
                {messageContentDetails?.data?.dataItems?.ActivityCode ?? ""}
              </span>
            </div>
            <div className="epts-body">
              <iframe
                className="mte-ieditor-iframe"
                id="editor"
                height="1130px"
                width="100%"
              />
            </div>
          </div>
        </$Skeleton>
      </div>
      <div className="drawer-footer-fixed align-content-center justify-content-between">
        <Pagination
          dataLength={templateHtmlContent?.length}
          currentPage={currentPage}
          onNavigation={onNavigation}
        />
        <div>
          <$Button
            className="mr-2"
            type="primary"
            htmlType="submit"
            disabled={domainMessageFormattedTemplate?.isLoading}
            onClick={sendDashboardMessage}
          >
            {t("US.COLLECTION.COMMON:COMMON.SEND")}
          </$Button>
          <$Button
            data-testid="drawer-cancel-btn"
            onClick={closeTemplateDrawer}
          >
            {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
          </$Button>
        </div>
      </div>
    </$Drawer>
  );
};

export default PDFTemplateEditor;
